import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ClearIcon from '@mui/icons-material/Clear';

import { loadServiceDetailsByService } from '../../../store/directusService';
import { searchUsersByLoggedInUserServiceIds } from '../../../store/userService';
import MultiSelect from '../../formElements/MultiSelect';
import { getServiceIds } from '../utils/calendarUtils';

import screen from '../styles/schedulingAssistant.module.css';

const SchedulingAssistantAdviserPanel = ({
    advisers,
    selectedAdvisers,
    removeSelectedAdviser,
    onUpdateSelectedAdvisers,
    activeAdviser,
    onChangeActiveAdviser,
    disabled = false
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [calendarHeight, setCalendarHeight] = useState(1000);
    const [listItemHeight, setListItemHeight] = useState(30);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const usersMetaData = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIdsMetaData
    );
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );

    // USE EFFECTS
    useEffect(() => {
        const el = document.querySelector(`.${screen.timeviewContentContainer}`);
        el && setCalendarHeight(el.getBoundingClientRect().height - 170);
    });

    useEffect(() => {
        setListItemHeight(~~(calendarHeight / selectedAdvisers.length));
    }, [selectedAdvisers]);

    useEffect(() => {
        if (activeAdviser) {
            const serviceIds = getServiceIds(activeAdviser);
            const missingServiceDetailIds = serviceIds.filter((el) => !(el in serviceDetailsById));
            if (missingServiceDetailIds.length)
                dispatch(loadServiceDetailsByService(missingServiceDetailIds));
        }
    }, [activeAdviser]);

    // EVENT HANDLERS
    const onToggleActiveAdviser = (adviser) => {
        adviser.id === activeAdviser.id
            ? onChangeActiveAdviser('')
            : onChangeActiveAdviser(adviser);
    };

    const onRemoveAdviser = (id, e) => {
        e.stopPropagation();
        removeSelectedAdviser(selectedAdvisers.filter((el) => el.id !== id));
        id === activeAdviser.id && onChangeActiveAdviser('');
    };

    const onLoadMoreAdvisers = () => {
        if (usersMetaData?.last) return;
        dispatch(
            searchUsersByLoggedInUserServiceIds(
                loggedInUser.serviceIds,
                !users.length ? 0 : usersMetaData.number + 1
            )
        );
    };

    // RENDER
    return (
        <div className={screen.adviserPanel}>
            <div>
                <MultiSelect
                    id="adviserIds"
                    label="Advisers"
                    placeholder="Select advisers..."
                    disabled={disabled}
                    mandatory={true}
                    capitalizeList={false}
                    customClass="absolutePosition"
                    autoClose={true}
                    menuItems={advisers || []}
                    preSelectedIds={selectedAdvisers.map((el) => el.id)}
                    preSelects={selectedAdvisers}
                    onChange={onUpdateSelectedAdvisers}
                    onLoadMoreItems={onLoadMoreAdvisers}
                    moreItemsToLoad={!usersMetaData.last}
                />
            </div>
            <div className={screen.selectedAdvisers}>
                {selectedAdvisers.map((el) => (
                    <div
                        key={el.id}
                        data-testid={'selected-advisers'}
                        onClick={() => onToggleActiveAdviser(el)}
                        className={`${screen.adviserListItem} ${activeAdviser.id === el.id ? screen.selected : ''}`}
                        style={{ height: `${listItemHeight}px` }}>
                        <span
                            className={screen.clearIcon}
                            onClick={(e) => onRemoveAdviser(el.id, e)}>
                            <ClearIcon />
                        </span>
                        <span className={screen.title}>
                            {' '}
                            {el.firstName} {el.lastName}{' '}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

SchedulingAssistantAdviserPanel.propTypes = {
    advisers: PropTypes.arrayOf(PropTypes.object),
    selectedAdvisers: PropTypes.arrayOf(PropTypes.object),
    removeSelectedAdviser: PropTypes.func,
    onUpdateSelectedAdvisers: PropTypes.func,
    activeAdviser: PropTypes.object,
    disabled: PropTypes.bool,
    onChangeActiveAdviser: PropTypes.func
};

export default SchedulingAssistantAdviserPanel;
